import {Component, computed, effect, inject, input, output, signal} from '@angular/core';

import { ArrowIconComponent } from '@components/arrow-icon/arrow-icon.component';
import { ChatMessageData, ChatInput, ChatInputWithIndex, OptionSelectedEvent } from '../rebranded-single-selector/rebranded-single-selector.types';
import {AppConfig} from "../../../app.config";

const DUPLICATE_TICKET_QUESTION_ID = 'duplicateTicket';

@Component({
  selector: 'multi-select-selector',
  templateUrl: './multi-select-selector.component.html',
  standalone: true,
  imports: [ArrowIconComponent],
})
export class MultiSelectSelectorComponent {
  data = input<ChatMessageData>();
  isClosedTicket = input<boolean>(false);
  readonly appConfig = inject(AppConfig);

  optionSelected = output<OptionSelectedEvent>();

  selectedIndices = signal<Set<number>>(new Set());
  submitted = signal(false);

  sortedInputs = computed(() => {
    const inputs: ChatInput[] = this.data()?.get('Inputs') || [];
    return inputs
      .map((each: ChatInput, originalIndex: number) => ({ ...each, originalIndex }))
      .filter((each: ChatInputWithIndex) => each.type !== this.appConfig.Shared.Chat.Inputs.TEXT);
  });

  buttonInputs = computed(() => this.sortedInputs()
    .filter((each: ChatInputWithIndex) => each.type === this.appConfig.Shared.Chat.Inputs.BUTTON)
    .sort((a: ChatInputWithIndex, b: ChatInputWithIndex) => Number(!!a.isExclusive) - Number(!!b.isExclusive)));

  isEffectivelyClosed = computed(() => {
    if (!this.isClosedTicket()) return false;
    if (!this.data()) return false;
    return this.data()?.get('questionId') !== DUPLICATE_TICKET_QUESTION_ID;
  });

  hasSelection = computed(() => this.selectedIndices().size > 0);

  hasNonExclusiveSelection = computed(() => {
    const selected = this.selectedIndices();
    return this.buttonInputs().some((each: ChatInputWithIndex) => selected.has(each.originalIndex) && !each.isExclusive);
  });

  hasExclusiveSelection = computed(() => {
    const selected = this.selectedIndices();
    return this.buttonInputs().some((each: ChatInputWithIndex) => selected.has(each.originalIndex) && !!each.isExclusive);
  });

  isVisible = computed(() => !this.submitted() && !!this.data() && this.buttonInputs().length > 0);

  displayInputs = computed(() => {
    const selected = this.selectedIndices();
    const hasNonExclusive = this.hasNonExclusiveSelection();
    const hasExclusive = this.hasExclusiveSelection();
    return this.buttonInputs().map((each: ChatInputWithIndex) => ({
      ...each,
      isSelected: selected.has(each.originalIndex),
      isDimmed: (each.isExclusive && hasNonExclusive) || (!each.isExclusive && hasExclusive),
    }));
  });

  constructor() {
    effect(() => {
      this.data();
      this.selectedIndices.set(new Set());
      this.submitted.set(false);
    });
  }

  toggleOption(originalIndex: number): void {
    const item = this.sortedInputs().find((i: ChatInputWithIndex) => i.originalIndex === originalIndex);
    const current = new Set(this.selectedIndices());

    if (current.has(originalIndex)) {
      current.delete(originalIndex);
    } else {
      if (item?.isExclusive) {
        // Exclusive option: deselect all others
        current.clear();
      } else {
        // Regular option: deselect any exclusive options
        for (const idx of current) {
          const existing = this.sortedInputs().find((i: ChatInputWithIndex) => i.originalIndex === idx);
          if (existing?.isExclusive) {
            current.delete(idx);
          }
        }
      }
      current.add(originalIndex);
    }
    this.selectedIndices.set(current);
  }

  submit(): void {
    if (!this.hasSelection()) return;

    const selectedInputs = this.buttonInputs()
      .filter((each: ChatInputWithIndex) => this.selectedIndices().has(each.originalIndex));
    const selectedValues = selectedInputs.map((each: ChatInputWithIndex) => each.text);
    const message = selectedValues.join('|||');

    const firstInput = this.buttonInputs()[0];
    this.optionSelected.emit({
      chat: this.data(),
      index: firstInput?.originalIndex ?? 0,
      input: message,
    });
    this.submitted.set(true);
  }
}
